import { useCallback, useMemo, useState } from 'react';

import { apiFetch } from '@/lib/api';
import { getJson, getQueryData, setQueryData, useQuery } from '@/lib/query';
import { latestTime } from '@/lib/time';

export interface Project {
  id: string;
  title: string;
  script_preview: string;
  creative_brief_preview: string;
  status: string;
  production_count: number;
  created_at: string;
  updated_at?: string;
  production_type?: string;
}

export interface ActiveProduction {
  production_id: string;
  project_id: string;
  project_title?: string;
  status: string;
  phase?: string;
  started_at?: string;
}

export interface ProdHistory {
  id: string;
  project_id: string;
  project_title?: string;
  script_preview?: string;
  status?: string;
  production_type?: string;
  config?: { production_type?: string; genre_override?: string };
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  updated_at?: string;
}

const POLL_MS = 30_000;
const HISTORY_PAGE = 20;

const KEY_HAS_PROJECTS = 'dashboard:hasProjects';
const KEY_ACTIVE = 'dashboard:active';
const KEY_HISTORY = 'dashboard:history';

interface ActiveData {
  productions: ActiveProduction[];
  /** production_id → gate phase, for in-flight productions paused on the producer. */
  reviews: Record<string, string>;
}

interface HistoryData {
  items: ProdHistory[];
  nextCursor: string;
}

interface ProductionsPage {
  productions?: ProdHistory[];
  next_cursor?: string;
}

const fetchHistoryPage = (cursor = '') =>
  getJson<ProductionsPage>(() =>
    apiFetch(`/api/productions?limit=${HISTORY_PAGE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`),
  );

async function loadActive(): Promise<ActiveData> {
  const data = await getJson<{ productions?: ActiveProduction[] }>(() => apiFetch('/api/productions/active'));
  const productions = data.productions || [];
  const entries = await Promise.all(
    productions.map(async (p) => {
      try {
        const g = await getJson<{ waiting?: boolean; phase?: string }>(() => apiFetch(`/api/review/${p.production_id}`));
        return g?.waiting ? ([p.production_id, String(g.phase || 'review')] as const) : null;
      } catch {
        return null;
      }
    }),
  );
  return { productions, reviews: Object.fromEntries(entries.filter(Boolean) as [string, string][]) };
}

/** Refresh the newest page WITHOUT dropping older pages the user already loaded. */
async function refreshHistory(): Promise<HistoryData> {
  const page = await fetchHistoryPage();
  const fresh = page.productions || [];
  const prev = getQueryData<HistoryData>(KEY_HISTORY);
  if (!prev || prev.items.length <= HISTORY_PAGE) return { items: fresh, nextCursor: page.next_cursor || '' };
  const ids = new Set(fresh.map((p) => p.id));
  return { items: [...fresh, ...prev.items.filter((p) => !ids.has(p.id))], nextCursor: prev.nextCursor };
}

/**
 * Dashboard data: whether the user has projects, what's in flight, the run history
 * (paged), and — most importantly — which in-flight productions are PAUSED waiting
 * for the producer. A production sitting on a review gate waits up to 12 hours, so
 * surfacing that on the landing page is the single most action-relevant thing here.
 *
 * Everything is cached (lib/query), so returning to the dashboard renders at once
 * and refreshes in the background.
 */
export function useDashboard() {
  const hasProjectsQ = useQuery<boolean>(
    KEY_HAS_PROJECTS,
    () => getJson<{ projects?: Project[] }>(() => apiFetch('/api/projects?limit=1')).then((d) => (d.projects || []).length > 0),
    { staleMs: 60_000 },
  );
  const activeQ = useQuery<ActiveData>(KEY_ACTIVE, loadActive, { intervalMs: POLL_MS });
  const historyQ = useQuery<HistoryData>(KEY_HISTORY, refreshHistory, { intervalMs: POLL_MS });
  const [loadingMore, setLoadingMore] = useState(false);

  const active = useMemo(() => activeQ.data?.productions ?? [], [activeQ.data]);
  const reviews = useMemo(() => activeQ.data?.reviews ?? {}, [activeQ.data]);
  const history = useMemo(() => historyQ.data?.items ?? [], [historyQ.data]);
  const nextCursor = historyQ.data?.nextCursor ?? '';

  const loadMoreHistory = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchHistoryPage(nextCursor);
      setQueryData<HistoryData>(KEY_HISTORY, (prev) => {
        const items = prev?.items ?? [];
        const ids = new Set(items.map((p) => p.id));
        return {
          items: [...items, ...(page.productions || []).filter((p) => !ids.has(p.id))],
          nextCursor: page.next_cursor || '',
        };
      });
    } catch {
      /* the button stays available to retry */
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore]);

  /** production_id → type, so in-flight rows (which don't carry config) can be badged. */
  const typeById = useMemo(
    () =>
      Object.fromEntries(history.map((h) => [h.id, h.config?.production_type || h.production_type || 'generative'])) as Record<
        string,
        string
      >,
    [history],
  );

  /** Finished runs, newest first, with anything still in flight removed. */
  const past = useMemo(() => {
    const activeIds = new Set(active.map((a) => a.production_id));
    return history
      .filter((p) => !activeIds.has(p.id))
      .sort(
        (a, b) =>
          latestTime(b.updated_at, b.completed_at, b.started_at, b.created_at) -
          latestTime(a.updated_at, a.completed_at, a.started_at, a.created_at),
      );
  }, [history, active]);

  /** Productions waiting on the producer sort to the top — they are blocked on a human. */
  const activeSorted = useMemo(
    () => [...active].sort((a, b) => (reviews[b.production_id] ? 1 : 0) - (reviews[a.production_id] ? 1 : 0)),
    [active, reviews],
  );

  return {
    hasProjects: hasProjectsQ.data ?? false,
    active: activeSorted,
    past,
    reviews,
    typeById,
    loading: hasProjectsQ.isLoading,
    hasMoreHistory: !!nextCursor,
    loadMoreHistory,
    loadingMore,
  };
}
